import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogContent, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Divider, 
  IconButton,
  InputAdornment,
  Slide,
  styled,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSpring, animated } from '@react-spring/web';
import { syncReadingsWithBackend, signOut, initializeUser } from '../services/userReadingService';
import { getCharacters } from '../data/characters';
import { notifyAuthStateChange } from './UserReadings';
import { languages, useLanguage } from '../data/languages';

// Choose character colors (using The Priestess colors by default)

const allCharacters = getCharacters('en'); // Use default language for colors
const characterColors = allCharacters[Math.floor(Math.random() * allCharacters.length)].colors;

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    maxWidth: '900px',
    margin: theme.spacing(2),
    boxShadow: '0 24px 38px rgba(0,0,0,0.14), 0 9px 46px rgba(0,0,0,0.12), 0 11px 15px rgba(0,0,0,0.2)',
  }
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${characterColors[0]} 0%, ${characterColors[3]} 100%)`,
  color: 'white',
  padding: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'url(/tarot-background.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.2,
    zIndex: 0,
  }
}));

const RightPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const FormField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    }
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
    transform: 'translateY(-2px)',
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  color: 'white',
  zIndex: 10,
}));


// Transition component for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Main component
export default function LoginModal({ open, onClose, onUserLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentLang = useLanguage(); // 响应式语言获取
  const config = languages[currentLang] || languages['en']; // 获取语言配置，带fallback
  
  // Animation for form transitions
  const formAnimation = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(20px)' },
    reset: false,
    config: { tension: 300, friction: 20 },
  });
  
  // Add a useEffect to reset the component state when the modal opens
  useEffect(() => {
    if (open) {
      // Reset all state values when modal opens
      setIsLogin(true);
      setShowPassword(false);
      setEmail('');
      setPassword('');
      setName('');
      setError(null);
      setUser(null);
    }
  }, [open]);
  
  // Check if user is already authenticated when modal opens
  useEffect(() => {
    if (open) {
      checkExistingAuth();
    }
  }, [open]);

  const checkExistingAuth = async () => { // 检查认证状态 (guide.md模式)
    try {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('aitarot_user');
      
      if (!token || !storedUser) {
        console.log('No stored auth found');
        return;
      }
      
      // 验证JWT token有效性
      const response = await fetch('/auth/me', {
        headers: {'Authorization': `Bearer ${token}`}
      });
      
      if (response.ok) {
        const userData = JSON.parse(storedUser);
        console.log('Valid auth found:', userData);
        await handleLoginSuccess(userData);
      } else {
        // Token无效，清理存储
        localStorage.removeItem('access_token');
        localStorage.removeItem('aitarot_user');
      }
    } catch (error) {
      console.log('Auth check failed:', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('aitarot_user');
    }
  };

  // Google Sign-In Button初始化 (按钮登录模式)
  useEffect(() => {
    if (!open) {
      return;
    }

    const initGoogleButton = () => {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '455731314429-8bull2i2on87t38ptcb4aoun657sq9l3.apps.googleusercontent.com';

      if (!clientId) {
        console.error('No client ID available');
        return;
      }

      // Note: Authentication check is handled separately in checkExistingAuth useEffect
      // If user is already authenticated, don't show Google button
      if (user) {
        console.log('User already authenticated, not showing Google button');
        return;
      }

      if (typeof google !== 'undefined' && google.accounts && google.accounts.id) {
        try {
          google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse
          });

          setTimeout(() => {
            const buttonEl = document.querySelector('#g_id_signin');
            if (buttonEl) {
              console.log('Rendering button, element state:', buttonEl.innerHTML);
              buttonEl.innerHTML = '';

              try {
                google.accounts.id.renderButton(buttonEl, {
                  theme: 'outline',
                  size: 'large',
                  type: 'standard',
                  text: 'signin_with',
                  logo_alignment: 'left'
                });
                console.log('renderButton called successfully');

                // 验证按钮渲染 - 更简单的检查
                setTimeout(() => {
                  const hasContent = buttonEl.innerHTML.trim().length > 0;
                  console.log('Button has content:', hasContent, 'HTML:', buttonEl.innerHTML);

                  if (!hasContent) {
                    console.error('Google button appears empty, trying fallback');
                    buttonEl.innerHTML = '<button style="padding: 10px 20px; background: #4285f4; color: white; border: none; border-radius: 4px; cursor: pointer;">Sign in with Google</button>';
                    buttonEl.querySelector('button').onclick = () => google.accounts.id.prompt();
                  } else {
                    console.log('Google button rendered successfully');
                  }
                }, 1000);
              } catch (error) {
                console.error('renderButton failed:', error);
                buttonEl.innerHTML = '<div style="color: red; font-size: 0.875rem;">Google Sign-In Error: ' + error.message + '</div>';
              }
            } else {
              console.error('Button element not found');
            }
          }, 500);
        } catch (error) {
          console.error('Google init error:', error);
        }
      } else {
        setTimeout(initGoogleButton, 500);
      }
    };

    // 延迟初始化以确保模态框完全渲染
    const timer = setTimeout(initGoogleButton, 100);

    return () => clearTimeout(timer); // 清理定时器
  }, [open, user]);

  const handleCredentialResponse = async (response) => { // Google Sign-In按钮回调 (按钮登录模式)
    console.log('Google credential received:', response);
    
    try {
      setIsGoogleAuthLoading(true);
      setError(null);
      
      // 发送到后端验证 (guide.md格式)
      const authResponse = await fetch('/auth/google', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({credential: response.credential})  // 使用credential字段
      });

      if (!authResponse.ok) {
        const errorData = await authResponse.json();
        throw new Error(errorData.message || '认证失败');
      }

      const data = await authResponse.json();
      
      if (data.success) {
        // 存储JWT token (guide.md模式)
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('aitarot_user', JSON.stringify(data.user));
        
        await handleLoginSuccess(data.user);
      } else {
        throw new Error(data.message || 'Google登录失败');
      }
    } catch (error) {
      console.error('Google auth error:', error);
      const errorMsg = error.message.includes('fetch') ? 
        '网络连接失败，请重试' : 
        error.message || 'Google登录失败';
      setError(errorMsg);
    } finally {
      setIsGoogleAuthLoading(false);
    }
  };


  const handleLoginSuccess = async (userData) => {
    try {
      setIsLoading(true);
      setIsGoogleAuthLoading(false);
      setError(null);

      // Map user data to expected format
      const mappedUserData = {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        provider: userData.provider || 'google',
        imageUrl: userData.picture || userData.imageUrl
      };

      // 如果数据不是来自localStorage，则存储到localStorage
      if (!localStorage.getItem('aitarot_user')) {
        localStorage.setItem('aitarot_user', JSON.stringify(userData));
      }

      // 尝试同步数据到服务器（失败也没关系，使用本地模式）
      try {
        await initializeUser(mappedUserData);
      } catch (e) {
        console.warn('Server sync failed, using local-only mode:', e);
      }

      setUser(mappedUserData);
      notifyAuthStateChange();

      setTimeout(() => {
        onUserLogin?.(mappedUserData);
        setTimeout(() => onClose(), 800);
      }, 500);
    } catch (error) {
      console.error('Login error:', error);
      setError('登录过程中出现问题，但您的账户将在本地工作。');
    } finally {
      setIsLoading(false);
      setIsGoogleAuthLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Basic validation - only check if email contains @ symbol
      if (!email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      if (!isLogin) {
        // Registration mode
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters long');
        }
        if (!name.trim()) {
          setName(email.split('@')[0]);
        }

        // Call registration endpoint
        const registerResponse = await fetch(`/auth/email/register`, { // 使用相对路径让代理生效
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            password: password,
            name: name.trim() || email.split('@')[0]
          })
        });

        const registerData = await registerResponse.json();

        if (!registerData.success) {
          throw new Error(registerData.message || 'Registration failed');
        }

        // After successful registration, automatically log in
        console.log('Registration successful, attempting login...');
      }

      // Login mode (or after successful registration)
      if (isLogin || !isLogin) {
        const loginResponse = await fetch(`/auth/email/login`, { // 使用相对路径让代理生效
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            password: password
          })
        });

        const loginData = await loginResponse.json();

        if (!loginData.success) {
          throw new Error(loginData.message || 'Login failed');
        }

        // Use the user data from the login response
        await handleLoginSuccess(loginData.user);
      }

    } catch (error) {
      console.error('Email auth error:', error);
      let errorMsg = `${isLogin ? '登录' : '注册'}失败`;
      
      if (error.message.includes('fetch')) {
        errorMsg = '网络连接失败，请检查网络后重试';
      } else if (error.message.includes('Server') || error.message.includes('500')) {
        errorMsg = '服务器暂时不可用，请稍后重试或使用本地模式';
      } else if (error.message.includes('Invalid') || error.message.includes('400')) {
        errorMsg = '输入信息有误，请检查邮箱和密码格式';
      } else if (error.message.includes('exists') || error.message.includes('409')) {
        errorMsg = '该邮箱已注册，请直接登录或使用找回密码';
      } else if (error.message.includes('not found') || error.message.includes('401')) {
        errorMsg = '邮箱或密码错误，请重试';
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Toggle between login and signup views
  const handleToggleView = () => {
    setIsLogin(!isLogin);
    setError(null);
  };
  
  // 登出功能 (guide.md模式)
  const handleLogout = () => {
    localStorage.removeItem('access_token');  // 清除JWT token
    localStorage.removeItem('aitarot_user');
    google.accounts.id.disableAutoSelect();  // 禁用Google自动选择
    signOut(); // 清除其他用户状态
    setUser(null);
    notifyAuthStateChange();
  };
  
  const handleClose = () => {
    setIsLogin(true);
    setEmail('');
    setPassword('');
    setName('');
    setError(null);
    setIsLoading(false);
    setUser(null);
    onClose();
  };
  
  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
      aria-labelledby="login-dialog-title"
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
        
        {!isMobile && (
          <LeftPanel sx={{ width: '40%' }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h3" component="h1" sx={{ 
                mb: 3,
                fontFamily: "'Recoleta', serif",
              }}>
                {config.loginModal.title}
              </Typography>
              <Typography variant="h5" component="h2" sx={{ 
                mb: 4,
                fontFamily: "'EB Garamond', serif",
              }}>
                {isLogin ? config.loginModal.welcomeBack : config.loginModal.joinCommunity}
              </Typography>
              <Typography variant="body1" sx={{ 
                mb: 2,
                fontFamily: "'EB Garamond', serif",
                fontSize: '1.1rem'
              }}>
                {isLogin 
                  ? config.loginModal.signInDescription
                  : config.loginModal.signUpDescription}
              </Typography>
            </Box>
          </LeftPanel>
        )}
        
        <RightPanel sx={{ width: isMobile ? '100%' : '60%' }}>
          {user ? (
            // Show user profile after successful login
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                欢迎, {user.name}!
              </Typography>
              {user.imageUrl && (
                <Box sx={{ mb: 3 }}>
                  <img 
                    src={user.imageUrl} 
                    alt={user.name} 
                    style={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }} 
                  />
                </Box>
              )}
              <Typography variant="body1" sx={{ mb: 4 }}>
                您已成功登录。
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleClose}
                >
                  继续
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary"
                  onClick={handleLogout}
                >
                  登出
                </Button>
              </Box>
            </Box>
          ) : (
            // Show login/signup form
            <>
              <Typography variant="h4" component="h2" sx={{ 
                mb: 1,
                fontFamily: "'Recoleta', serif",
                textAlign: 'center'
              }}>
                {isLogin ? config.loginModal.signIn : config.loginModal.createAccount}
              </Typography>
              
              <Typography variant="body1" sx={{ 
                mb: 4,
                color: 'text.secondary',
                textAlign: 'center',
                fontFamily: "'EB Garamond', serif",
                fontSize: '1.1rem'
              }}>
                {isLogin ? 
                  config.loginModal.signInPrompt : 
                  config.loginModal.signUpPrompt}
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              <animated.div style={formAnimation}>
                <form onSubmit={handleEmailLogin}>
                  {!isLogin && (
                    <FormField
                      label="Full Name"
                      variant="outlined"
                      fullWidth
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      required={!isLogin}
                      disabled={isLoading}
                    />
                  )}
                  
                  <FormField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={isLoading}
                    error={!!error && error.includes('email')}
                    helperText={error && error.includes('email') ? error : ""}
                    inputProps={{
                      pattern: ".*@.*",
                      title: "Please include an @ in the email address"
                    }}
                  />
                  
                  <FormField
                    label="Password"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isLogin ? "Enter your password" : "Create a password"}
                    required
                    disabled={isLoading}
                    error={!!error && error.includes('password')}
                    helperText={error && error.includes('password') ? error : ""}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={isLoading}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  
                  {isLogin && (
                    <Box sx={{ textAlign: 'right', mb: 2 }}>
                      <Button 
                        variant="text" 
                        color="primary"
                        disabled={isLoading}
                        sx={{ 
                          textTransform: 'none',
                          fontFamily: "'EB Garamond', serif",
                          fontSize: '1rem'
                        }}
                      >
                        Forgot password?
                      </Button>
                    </Box>
                  )}
                  
                  <SubmitButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isLoading ? 
                      (isLogin ? config.loginModal.signingIn : config.loginModal.creatingAccount) : 
                      (isLogin ? config.loginModal.signIn : config.loginModal.createAccount)}
                  </SubmitButton>
                </form>
              </animated.div>
              
              <Box sx={{ my: 3, display: 'flex', alignItems: 'center' }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
                  OR
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>
              
              {/* Google Sign-In Button (按钮登录模式) */}
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 3 }}>
                <div id="g_id_signin" style={{
                  minHeight: '40px',
                  maxWidth: '280px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'box-shadow 0.2s ease'
                }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    Loading Google Sign-In...
                  </Typography>
                </div>
                {isGoogleAuthLoading && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.9)',
                    borderRadius: '8px',
                    backdropFilter: 'blur(2px)'
                  }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">{config.loginModal.signingIn || '登录中...'}</Typography>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {isLogin ? config.loginModal.noAccount : config.loginModal.haveAccount}
                </Typography>
                <Button 
                  variant="text" 
                  color="primary" 
                  onClick={handleToggleView}
                  disabled={isLoading}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    fontFamily: "'EB Garamond', serif",
                    fontSize: '1.2rem',
                    padding: '8px 16px'
                  }}
                >
                  {isLogin ? config.loginModal.signUpButton : config.loginModal.signInButton}
                </Button>
              </Box>
            </>
          )}
        </RightPanel>
      </DialogContent>
    </StyledDialog>
  );
} 